import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader } from "../components/Shared";
import { fmtFCFA, fmtDate } from "../lib/format";
import { Briefcase, Users, TrendingUp, CreditCard, Receipt, FileCheck } from "lucide-react";

export default function SearchResults() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [res, setRes] = useState(null);

  useEffect(() => {
    if (q) api.get(`/search?q=${encodeURIComponent(q)}`).then(({ data }) => setRes(data));
  }, [q]);

  const total = res ? Object.values(res).reduce((a, b) => a + b.length, 0) : 0;

  return (
    <div>
      <PageHeader title={`Search results for “${q}”`} subtitle={res ? `${total} matching record${total === 1 ? "" : "s"}` : "Searching…"} />

      {res && total === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-16 text-center text-slate-400">
          No records matched your search.
        </div>
      )}

      <div className="space-y-6">
        <Section title="Projects" icon={Briefcase} items={res?.projects} render={(p) => (
          <Link to={`/projects/${p.id}`} key={p.id} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50">
            <div><div className="font-medium text-slate-800">{p.name}</div><div className="text-xs text-slate-400">{p.category} · {p.owner}</div></div>
            <span className="font-mono-num text-sm text-slate-600">{fmtFCFA(p.total_cost)}</span>
          </Link>
        )} />
        <Section title="Employees" icon={Users} items={res?.employees} render={(e) => (
          <div key={e.id} className="flex items-center justify-between px-5 py-3">
            <div><div className="font-medium text-slate-800">{e.name}</div><div className="text-xs text-slate-400">{e.profession}</div></div>
            <span className="text-sm text-slate-500">{e.phone}</span>
          </div>
        )} />
        <Section title="Income" icon={TrendingUp} items={res?.income} render={(i) => (
          <div key={i.id} className="flex items-center justify-between px-5 py-3">
            <div><div className="font-medium text-slate-800">{i.project_name}</div><div className="text-xs text-slate-400">{fmtDate(i.date)}</div></div>
            <span className="font-mono-num text-sm text-emerald-700">{fmtFCFA(i.amount)}</span>
          </div>
        )} />
        <Section title="Payments" icon={CreditCard} items={res?.payments} render={(p) => (
          <div key={p.id} className="flex items-center justify-between px-5 py-3">
            <div><div className="font-medium text-slate-800">{p.purpose}</div><div className="text-xs text-slate-400">{p.employee_name || p.project_name}</div></div>
            <span className="font-mono-num text-sm text-slate-700">{fmtFCFA(p.amount)}</span>
          </div>
        )} />
        <Section title="Expenditures" icon={Receipt} items={res?.expenditures} render={(x) => (
          <div key={x.id} className="flex items-center justify-between px-5 py-3">
            <div><div className="font-medium text-slate-800">{x.purpose}</div><div className="text-xs text-slate-400">{x.project_name || "—"}</div></div>
            <span className="font-mono-num text-sm text-amber-700">{fmtFCFA(x.amount)}</span>
          </div>
        )} />
        <Section title="Payment Checks" icon={FileCheck} items={res?.checks} render={(c) => (
          <Link to="/checks" key={c.id} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50">
            <div><div className="font-medium text-slate-800">{c.check_number} · {c.beneficiary_name}</div><div className="text-xs text-slate-400">{c.purpose}</div></div>
            <span className="font-mono-num text-sm text-slate-700">{fmtFCFA(c.amount)}</span>
          </Link>
        )} />
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, items, render }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 text-sm font-semibold text-slate-700">
        <Icon className="h-4 w-4" /> {title} ({items.length})
      </div>
      <div className="divide-y divide-slate-100">{items.map(render)}</div>
    </div>
  );
}
